// ...existing code...
/**
 * Document Agent - API calls and React hook for document management.
 * Handles document generation, listing, signing workflow, and templates.
 */

import { useState, useCallback } from 'react';
import APIAgent from './apiAgent';

import { API_BASE_URL } from './apiAgent';

// --- API Methods (static) ---

class DocumentAPI {
  static async generateDocument(offerId, templateType = 'purchase_agreement') {
    try {
      return await APIAgent.request('/api/documents/generate', 'POST', {
        offer_id: offerId,
        template_type: templateType,
      });
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  }

  static async listDocuments(offerId = null, status = null, page = 1, perPage = 20) {
    try {
      const params = new URLSearchParams();
      if (offerId) params.append('offer_id', offerId);
      if (status) params.append('status', status);
      params.append('page', page);
      params.append('per_page', perPage);
      return await APIAgent.request(`/api/documents?${params.toString()}`);
    } catch (error) {
      console.error('Error listing documents:', error);
      throw error;
    }
  }

  static async getDocument(documentId) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}`);
    } catch (error) {
      console.error('Error getting document:', error);
      throw error;
    }
  }

  /**
   * Reconcile live DocuSign envelope status and return the refreshed document.
   *
   * Parallel to the listing agreement's reconcile: the DocuSign Connect webhook
   * is the durable completion path, but callbacks can be dropped (or rejected in
   * an env whose Connect HMAC key is unset), which would strand a fully-signed
   * envelope at "sent". This asks the server to read the authoritative envelope
   * status on demand and finalize the whole bundle. Idempotent with the webhook,
   * and returns the same shape as {@link getDocument}.
   */
  static async reconcileDocument(documentId) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}/reconcile`, 'POST');
    } catch (error) {
      console.error('Error reconciling document:', error);
      throw error;
    }
  }

  /**
   * Fetch a document's PDF as a Blob. For a completed DocuSign document the
   * backend serves the archived signed copy, not the pre-signature render.
   */
  static async fetchDocumentPdf(documentId) {
    const response = await fetch(`${API_BASE_URL}/api/documents/${documentId}/download`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    return response.blob();
  }

  static async downloadDocument(documentId) {
    try {
      const blob = await DocumentAPI.fetchDocumentPdf(documentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `document_${documentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw error;
    }
  }

  static async sendDocument(documentId, signerIds) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}/send`, 'POST', {
        signer_ids: signerIds,
      });
    } catch (error) {
      console.error('Error sending document:', error);
      throw error;
    }
  }

  static async getSigningContext(documentId) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}/signing`);
    } catch (error) {
      console.error('Error getting signing context:', error);
      throw error;
    }
  }

  static async signDocument(documentId, typedName) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}/sign`, 'POST', {
        typed_name: typedName,
      });
    } catch (error) {
      console.error('Error signing document:', error);
      throw error;
    }
  }

  static async declineDocument(documentId, reason) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}/decline`, 'POST', {
        reason,
      });
    } catch (error) {
      console.error('Error declining document:', error);
      throw error;
    }
  }

  static async voidDocument(documentId, reason) {
    try {
      return await APIAgent.request(`/api/documents/${documentId}/void`, 'POST', {
        reason,
      });
    } catch (error) {
      console.error('Error voiding document:', error);
      throw error;
    }
  }

  static async listTemplates(templateType = null) {
    try {
      const params = templateType ? `?template_type=${templateType}` : '';
      return await APIAgent.request(`/api/document-templates${params}`);
    } catch (error) {
      console.error('Error listing templates:', error);
      throw error;
    }
  }
}

// --- React Hook ---

export const useDocuments = (offerId = null) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [signingContext, setSigningContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const fetchDocuments = useCallback(async (pageNum = 1, status = null) => {
    try {
      setLoading(true);
      setError(null);
      const data = await DocumentAPI.listDocuments(offerId, status, pageNum);
      setDocuments(data.documents || []);
      setTotal(data.total || 0);
      setPage(pageNum);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  const generateDocument = useCallback(async (targetOfferId, templateType) => {
    try {
      setLoading(true);
      setError(null);
      const result = await DocumentAPI.generateDocument(targetOfferId, templateType);
      await fetchDocuments(page);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchDocuments, page]);

  const viewDocument = useCallback(async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const doc = await DocumentAPI.getDocument(documentId);
      setSelectedDocument(doc);
      return doc;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadDocument = useCallback(async (documentId) => {
    try {
      setError(null);
      await DocumentAPI.downloadDocument(documentId);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const sendForSigning = useCallback(async (documentId, signerIds) => {
    try {
      setLoading(true);
      setError(null);
      const result = await DocumentAPI.sendDocument(documentId, signerIds);
      await fetchDocuments(page);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchDocuments, page]);

  const fetchSigningContext = useCallback(async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const ctx = await DocumentAPI.getSigningContext(documentId);
      setSigningContext(ctx);
      return ctx;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signDocument = useCallback(async (documentId, typedName) => {
    try {
      setLoading(true);
      setError(null);
      const result = await DocumentAPI.signDocument(documentId, typedName);
      await fetchDocuments(page);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchDocuments, page]);

  const declineDocument = useCallback(async (documentId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const result = await DocumentAPI.declineDocument(documentId, reason);
      await fetchDocuments(page);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchDocuments, page]);

  const voidDocument = useCallback(async (documentId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const result = await DocumentAPI.voidDocument(documentId, reason);
      await fetchDocuments(page);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchDocuments, page]);

  return {
    documents,
    selectedDocument,
    signingContext,
    loading,
    error,
    total,
    page,
    fetchDocuments,
    generateDocument,
    viewDocument,
    downloadDocument,
    sendForSigning,
    fetchSigningContext,
    signDocument,
    declineDocument,
    voidDocument,
    setSelectedDocument,
  };
};

export { API_BASE_URL };
export default DocumentAPI;
